import { useState } from 'preact/hooks';
import { websApi } from '@/api/webs';
import { useToast } from '@/hooks/useToast';
export function useWebSignOff({ workspaceId, campaignId, webId, web, setWeb, hasBigMarker }) {
    const { showError, showSuccess } = useToast();
    const [showSignOffModal, setShowSignOffModal] = useState(false);
    const [showReopenModal, setShowReopenModal] = useState(false);
    const [signingOff, setSigningOff] = useState(false);
    const [reopening, setReopening] = useState(false);
    /**
     * Runs a Web mutation that requires an ETag for optimistic concurrency. If the
     * server rejects with 412 (Precondition Failed) the Web doc was updated since
     * we loaded it — re-fetch the latest state and retry once with the fresh ETag.
     * Sign-off / reopen are intent-based ("I want this signed off / reopened"), so
     * applying them to the fresh state is the right behavior; failing the user with
     * a "concurrency error" toast would just send them off to refresh and click again.
     */
    const attemptWithEtagRetry = async (attempt, currentETag) => {
        let res = await attempt(currentETag);
        if (res.status === 412) {
            const fresh = await websApi.getById(workspaceId, webId);
            if (!fresh.error && fresh.data) {
                setWeb(fresh.data);
                res = await attempt(fresh.data.ETag);
            }
        }
        return res;
    };
    const handleSignOff = async () => {
        if (!web)
            return;
        setSigningOff(true);
        const res = await attemptWithEtagRetry(etag => websApi.signOff(workspaceId, campaignId, webId, { ETag: etag, WorkspaceId: workspaceId, Confirmed: true }), web.ETag);
        if (!res.error && res.data) {
            setWeb(res.data);
            showSuccess('Signed off', hasBigMarker ? 'Web has been signed off and synced to BigMarker.' : 'Web has been signed off.');
            setShowSignOffModal(false);
        }
        else if (res.status !== 422) {
            showError('Sign-off failed', res.error || 'Unable to sign off web');
        }
        setSigningOff(false);
    };
    const handleReopen = async () => {
        if (!web)
            return;
        setReopening(true);
        const res = await attemptWithEtagRetry(etag => websApi.reopen(workspaceId, campaignId, webId, { ETag: etag, WorkspaceId: workspaceId }), web.ETag);
        if (!res.error && res.data) {
            setWeb(res.data);
            showSuccess('Reopened', 'Web has been reopened for editing.');
            setShowReopenModal(false);
        }
        else if (res.status !== 422) {
            showError('Reopen failed', res.error || 'Unable to reopen web');
        }
        setReopening(false);
    };
    return {
        showSignOffModal,
        setShowSignOffModal,
        showReopenModal,
        setShowReopenModal,
        handleSignOff,
        handleReopen,
        signingOff,
        reopening,
    };
}
